import { Link, useRouter } from "@tanstack/react-router";
import {
  AlertCircle,
  Briefcase,
  CheckCircle2,
  ClipboardCheck,
  HardHat,
  Loader2,
  Lock,
  Mail,
  UserCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { authClient, signIn } from "@/lib/auth-client";

type AuthMethod = "password" | "magic-link";

interface DemoAccount {
  label: string;
  email: string;
  password: string;
  icon: React.ReactNode;
}

const DEMO_ACCOUNTS: DemoAccount[] = [
  {
    label: "Admin",
    email: "a@demo.com",
    password: "A",
    icon: <UserCircle className="size-4" />,
  },
  {
    label: "Project Manager",
    email: "p@demo.com",
    password: "P",
    icon: <HardHat className="size-4" />,
  },
  {
    label: "Contractor",
    email: "c@demo.com",
    password: "C",
    icon: <Briefcase className="size-4" />,
  },
  {
    label: "Supervisor",
    email: "s@demo.com",
    password: "S",
    icon: <ClipboardCheck className="size-4" />,
  },
];

const ERROR_MESSAGES: Record<string, string> = {
  expired_token: "This sign-in link has expired. Please request a new one.",
  invalid_token: "This sign-in link is invalid. Please request a new one.",
};

export function LoginPage() {
  const router = useRouter();

  const [method, setMethod] = useState<AuthMethod>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const verifyMagicLink = useCallback(
    async function verifyMagicLink(token: string) {
      try {
        const res = await fetch("/api/magic-link/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });

        if (!res.ok) {
          const data: { error?: string } = await res.json();
          const errorKey = typeof data.error === "string" ? data.error : "";
          setError(ERROR_MESSAGES[errorKey] ?? "Failed to verify sign-in link.");
          setVerifying(false);
          return;
        }

        router.navigate({ to: "/" });
      } catch {
        setError("Failed to verify sign-in link. Please try again.");
        setVerifying(false);
      }
    },
    [router],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const authError = params.get("auth_error");
    if (authError) {
      setError(ERROR_MESSAGES[authError] ?? "An authentication error occurred.");
      return;
    }

    const magicToken = params.get("magic_token");
    if (magicToken) {
      setVerifying(true);
      verifyMagicLink(magicToken);
    }
  }, [verifyMagicLink]);

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await signIn.email({ email, password });
      if (result.error) {
        setError(result.error.message ?? "Invalid credentials");
      } else {
        const session = await authClient.getSession();
        const role = session.data?.user?.role;
        router.navigate({ to: role === "supervisor" ? "/daily-logs" : "/" });
      }
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        const data: { error?: string } = await res.json();
        setError(typeof data.error === "string" ? data.error : "Failed to send sign-in link.");
      } else {
        setSent(true);
      }
    } catch {
      setError("Failed to send sign-in link. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function fillDemoCredentials(account: DemoAccount) {
    setEmail(account.email);
    setPassword(account.password);
    setMethod("password");
    setError("");
  }

  if (verifying) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-slate-950">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/login-bg.jpg')" }}
        />
        <div className="absolute inset-0 bg-slate-950/80" />
        <div className="relative z-10 flex flex-col items-center gap-4 text-white">
          <Loader2 className="size-8 animate-spin text-[#778DA9]" />
          <p className="text-lg font-medium">Verifying your sign-in link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-slate-950">
      {/* Background photo */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/login-bg.jpg')", filter: "brightness(1.75)" }}
      />
      {/* Darkening overlay */}
      <div className="absolute inset-0 bg-slate-950/75" />
      {/* Bottom vignette for extra depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />

      <div className="relative z-10 w-full max-w-md px-4">
        {/* Branding */}
        <div className="mb-8 flex flex-col items-center">
          <img
            src="/logo-white.png"
            alt="Prominent Builders"
            className="h-auto w-72 drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]"
          />
          <p className="mt-3 text-sm tracking-wide text-slate-300">Builders Operating System</p>
        </div>

        <Card className="border-slate-700/50 bg-slate-800/80 shadow-2xl backdrop-blur">
          <CardContent className="space-y-6 pt-2">
            {/* Demo credentials */}
            <div className="rounded-lg bg-[#1B3A5C]/60 p-4 ring-1 ring-[#415A77]/30">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-[#778DA9]">
                Demo Accounts
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {DEMO_ACCOUNTS.map((account) => (
                  <button
                    key={account.email}
                    type="button"
                    onClick={() => fillDemoCredentials(account)}
                    className="flex flex-col items-center gap-1 rounded-md bg-[#415A77]/20 px-2 py-2.5 text-xs text-slate-300 transition-colors hover:bg-[#415A77]/40 hover:text-white"
                  >
                    {account.icon}
                    <span className="font-medium">{account.label}</span>
                    <span className="text-[10px] text-slate-500">{account.email}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Error display */}
            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-red-500/10 p-3 text-sm text-red-400 ring-1 ring-red-500/20">
                <AlertCircle className="mt-0.5 size-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <h2 className="text-lg font-semibold text-white">Sign In</h2>
            </div>

            {/* Method tabs */}
            <Tabs
              value={method}
              onValueChange={(v) => {
                setMethod(v as AuthMethod);
                setError("");
                setSent(false);
              }}
            >
              <TabsList className="w-full bg-slate-700/50">
                <TabsTrigger
                  value="password"
                  className="flex-1 data-[state=active]:bg-[#415A77] data-[state=active]:text-white"
                >
                  <Lock className="mr-1.5 size-3.5" />
                  Password
                </TabsTrigger>
                <TabsTrigger
                  value="magic-link"
                  className="flex-1 data-[state=active]:bg-[#415A77] data-[state=active]:text-white"
                >
                  <Mail className="mr-1.5 size-3.5" />
                  Email Sign-In
                </TabsTrigger>
              </TabsList>

              {/* Password form */}
              <TabsContent value="password">
                <form onSubmit={handlePasswordLogin} className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-300">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-500 focus-visible:ring-[#415A77]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-300">
                      Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-500 focus-visible:ring-[#415A77]"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#415A77] text-white hover:bg-[#4E6B8A]"
                  >
                    {loading ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                    Sign In
                  </Button>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500">
                      Or use <span className="text-[#778DA9]">Email Sign-In</span> for passwordless.
                    </p>
                    <Link to="/forgot-password" className="text-xs text-[#778DA9] hover:underline">
                      Forgot password?
                    </Link>
                  </div>
                </form>
              </TabsContent>

              {/* Magic link form */}
              <TabsContent value="magic-link">
                {sent ? (
                  <div className="mt-4 flex flex-col items-center gap-3 py-6 text-center">
                    <div className="rounded-full bg-green-500/10 p-3 ring-1 ring-green-500/20">
                      <CheckCircle2 className="size-8 text-green-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">Check your inbox</h3>
                    <p className="text-sm text-slate-400">
                      We sent a sign-in link to{" "}
                      <span className="font-medium text-white">{email}</span>
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSent(false);
                        setEmail("");
                      }}
                      className="mt-2 text-sm text-[#778DA9] underline-offset-4 hover:underline"
                    >
                      Use a different email
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleMagicLink} className="mt-4 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="magic-email" className="text-slate-300">
                        Email
                      </Label>
                      <Input
                        id="magic-email"
                        type="email"
                        placeholder="you@company.com"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-500 focus-visible:ring-[#415A77]"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#415A77] text-white hover:bg-[#4E6B8A]"
                    >
                      {loading ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                      Send Sign-In Link
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-slate-600">
          Prominent Builders OS &middot; Construction Management Platform
        </p>
      </div>
    </div>
  );
}
